/**
 * KOPS-461: Contact Intelligence Hub
 * aisearchportal.com/admin/contacts
 * Navy + Gold split-panel CRM layout
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faEnvelope, faPaperPlane, faFire, faUsers, faRocket,
  faChartLine, faStar, faSync, faCheckCircle, faCalendarAlt,
  faPhone, faBuilding, faStickyNote, faArrowRight, faFilter, faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

interface Contact {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  brokerage: string;
  source: string;
  lead_score: number;
  status: string;
  ai_summary: string | null;
  last_activity_at: string | null;
  created_at: string;
  activity_count: number;
  emails_sent: number;
}

interface Activity {
  id: string;
  activity_type: string;
  activity_data: any;
  created_at: string;
}

interface EmailRecord {
  id: string;
  subject: string;
  body: string;
  template: string;
  status: string;
  sent_at: string;
}

interface EventReg {
  event_title: string;
  event_date: string;
  event_time: string;
  attended: boolean;
  status: string;
}

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db'
};

const ContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Right panel
  const [selected, setSelected] = useState<Contact | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [emailHistory, setEmailHistory] = useState<EmailRecord[]>([]);
  const [contactEvents, setContactEvents] = useState<EventReg[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'timeline' | 'emails' | 'notes' | 'sms' | 'automation' | 'invite' | 'production'>('timeline');
  const [mlsProfile, setMlsProfile] = useState<any>(null);
  const [mlsProduction, setMlsProduction] = useState<any>(null);
  const [smsMessages, setSmsMessages] = useState<any[]>([]);
  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [automationData, setAutomationData] = useState<any>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [inviteEventId, setInviteEventId] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [jasperTask, setJasperTask] = useState('');
  const [jasperPreview, setJasperPreview] = useState('');
  const [jasperLoading, setJasperLoading] = useState(false);
  const [jasperSending, setJasperSending] = useState(false);
  const [jasperChannel, setJasperChannel] = useState<'sms' | 'email'>('sms');
  const [jasperEmailSubject, setJasperEmailSubject] = useState('');

  // Call
  const [teamCallers, setTeamCallers] = useState<any[]>([]);
  const [showCallPanel, setShowCallPanel] = useState(false);
  const [selectedCaller, setSelectedCaller] = useState('');
  const [calling, setCalling] = useState(false);

  // Actions
  const [noteText, setNoteText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', phone: '', brokerage: '' });
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);

  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);

  // Bulk email
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  // Check URL for search param and contactId (from event registrant click or refresh)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSearch = params.get('search');
    if (urlSearch) setSearch(urlSearch);
  }, []);

  useEffect(() => { fetchContacts(); }, [search, filterStatus]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const response = await axiosInstance.get(`/contacts?${params.toString()}`);
      if (response.data.success) {
        setContacts(response.data.contacts);
        setStats(response.data.stats);
        // Auto-select contact from URL param or search
        if (!selected) {
          const urlParams = new URLSearchParams(window.location.search);
          const contactId = urlParams.get('contactId');
          if (contactId) {
            const match = response.data.contacts.find((c: any) => c.id === contactId);
            if (match) selectContact(match);
          } else if (response.data.contacts.length === 1) {
            selectContact(response.data.contacts[0]);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectContact = async (contact: Contact) => {
    setSelected(contact);
    // Persist in URL for refresh
    const url = new URL(window.location.href);
    url.searchParams.set('contactId', contact.id);
    window.history.replaceState({}, '', url.toString());
    setDetailLoading(true);
    setShowEmailForm(false);
    setEditingContact(false);
    setActiveTab('timeline');
    try {
      const response = await axiosInstance.get(`/contacts/${contact.id}`);
      if (response.data.success) {
        setActivities(response.data.activities);
        setEmailHistory(response.data.emails);
        setContactEvents(response.data.events);
        setMlsProfile(null);
        setMlsProduction(null);
        setSmsMessages([]);
        setCallLogs([]);
        setAutomationData(null);
      }
    } catch (err) {
      console.error('Failed to fetch contact:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (contactId: string, newStatus: string) => {
    try {
      await axiosInstance.put(`/contacts/${contactId}`, { status: newStatus });
      fetchContacts();
      if (selected?.id === contactId) {
        setSelected({ ...selected, status: newStatus });
      }
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to update status' });
    }
  };

  const handleAddNote = async () => {
    if (!selected || !noteText.trim()) return;
    try {
      await axiosInstance.put(`/contacts/${selected.id}`, { notes: noteText });
      setNoteText('');
      selectContact(selected);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to add note' });
    }
  };

  const handleSendEmail = async () => {
    if (!selected || !emailSubject || !emailMessage) return;
    setSending(true);
    try {
      await axiosInstance.post(`/contacts/${selected.id}/email`, {
        subject: emailSubject, message: emailMessage
      });
      setAlert({ type: 'success', message: `Email sent to ${selected.full_name}` });
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
      selectContact(selected);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send' });
    } finally {
      setSending(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!bulkSubject || !bulkMessage) return;
    setBulkSending(true);
    try {
      const response = await axiosInstance.post('/contacts/bulk-email', {
        subject: bulkSubject, message: bulkMessage,
        status: filterStatus !== 'all' ? filterStatus : undefined
      });
      setAlert({ type: 'success', message: `Email sent to ${response.data.sent} contacts` });
      setShowBulkEmail(false);
      setBulkSubject('');
      setBulkMessage('');
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send' });
    } finally {
      setBulkSending(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post('/contacts/sync');
      setAlert({ type: 'success', message: response.data.message });
      fetchContacts();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const getScoreColor = (score: number) => score >= 50 ? colors.red : score >= 30 ? colors.orange : score >= 10 ? colors.blue : colors.muted;

  const getActivityIcon = (type: string) => {
    const icons: Record<string, any> = {
      event_registered: faCalendarAlt, event_attended: faCheckCircle, email_sent: faEnvelope,
      note_added: faStickyNote, synced_from_events: faSync, demo_requested: faRocket,
      lead_captured: faStar, sms_inbound: faPhone, sms_drip_followup: faArrowRight,
      event_invite_sent: faCalendarAlt, sms_noshow_followup: faArrowRight,
      jasper_outbound: faRocket, outbound_call: faPhone
    };
    return icons[type] || faCheckCircle;
  };

  const getActivityColor = (type: string) => {
    const c: Record<string, string> = {
      event_attended: colors.green, email_sent: colors.blue, note_added: colors.gold,
      synced_from_events: colors.muted, event_registered: colors.orange,
      demo_requested: '#e74c3c', lead_captured: colors.gold, sms_inbound: colors.blue,
      sms_drip_followup: colors.orange,
      event_invite_sent: colors.green, sms_noshow_followup: '#e67e22',
      jasper_outbound: colors.blue, outbound_call: colors.green
    };
    return c[type] || colors.muted;
  };

  const formatActivityText = (type: string, data: any) => {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    switch (type) {
      case 'note_added': return parsed?.note || 'Note added';
      case 'email_sent': return `Sent: "${parsed?.subject || ''}"`;
      case 'synced_from_events': return `Imported from events (${parsed?.events_attended || 0} attended)`;
      case 'demo_requested': return `🔥 Demo Requested — ${parsed?.note || `${parsed?.brokerage || 'Unknown company'}. Phone: ${parsed?.phone || 'not provided'}`}`;
      case 'lead_captured': return `Lead captured from ${parsed?.source || 'website'}${parsed?.brokerage ? ` — ${parsed.brokerage}` : ''}`;
      case 'sms_inbound': return `📱 SMS: "${parsed?.message?.substring(0, 80) || 'Text message received'}"`;
      case 'sms_drip_followup': return `🤖 Auto follow-up sent (drip ${parsed?.drip_number || ''}: ${parsed?.drip_step || ''})`;
      case 'event_invite_sent': return `📨 Jasper invited to: ${parsed?.event_title || 'event'}`;
      case 'sms_noshow_followup': return `🤖 No-show follow-up (Day ${parsed?.day || ''}: ${parsed?.step || parsed?.event_title || ''})`;
      case 'jasper_outbound': return `🤖 Jasper ${parsed?.channel === 'email' ? '✉️ Email' : '📱 SMS'}: ${parsed?.task || 'Message sent'}${parsed?.subject ? ` — Subject: "${parsed.subject}"` : ''}`;
      case 'outbound_call': return `📞 Outbound call by ${parsed?.caller || 'team member'}`;
      case 'event_registered': return `Registered for ${parsed?.event_title || 'event'}`;
      default: return type.replace(/_/g, ' ');
    }
  };

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 0' }}>
        <Container fluid className="px-4 d-flex justify-content-between align-items-center">
          <div>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal
              </span>
            </a>
            <span style={{ color: colors.muted, fontSize: '14px', marginLeft: '16px' }}>/ Contacts</span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a href="/admin/subscribers" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Subscribers</a>
            <a href="/admin/team" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Team</a>
            <a href="/admin/contacts" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '13px', padding: '6px 12px', fontWeight: 600 }}>Contacts</a>
            <a href="/admin/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Events</a>
            <a href="/admin/support" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Support</a>
            <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Dashboard</a>
          </div>
        </Container>
      </nav>

      {alert && (
        <div style={{ padding: '0 24px', paddingTop: '12px' }}>
          <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>
        </div>
      )}

      {/* Stats Strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '16px 24px' }}>
        <div className="d-flex gap-4 align-items-center">
          {stats && [
            { label: 'Total', value: stats.total, icon: faUsers, color: colors.blue },
            { label: 'Hot', value: stats.hot, icon: faFire, color: colors.red },
            { label: 'New', value: stats.new_contacts, icon: faStar, color: colors.gold },
            { label: 'Engaged', value: stats.engaged, icon: faChartLine, color: colors.orange },
            { label: 'Needs Follow-up', value: stats.needs_followup, icon: faPhone, color: '#e74c3c' },
            { label: 'SMS (24h)', value: stats.recent_sms, icon: faArrowRight, color: '#2ecc71' },
            { label: 'Converted', value: stats.converted, icon: faCheckCircle, color: colors.green }
          ].map((s, i) => (
            <div key={i} className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={s.icon} style={{ color: s.color, fontSize: '14px' }} />
              <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{s.value || 0}</span>
              <span style={{ color: colors.muted, fontSize: '12px' }}>{s.label}</span>
            </div>
          ))}
          <div className="ms-auto d-flex gap-2">
            <Button size="sm" onClick={handleSync} disabled={syncing} style={{ background: 'transparent', border: `1px solid ${colors.muted}`, color: colors.muted, fontSize: '12px' }}>
              <FontAwesomeIcon icon={faSync} className={syncing ? 'fa-spin' : ''} /> Sync
            </Button>
            <Button size="sm" onClick={() => setShowBulkEmail(true)} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '12px', fontWeight: 600 }}>
              <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Email All
            </Button>
          </div>
        </div>
      </div>

      {/* Main Split Panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 130px)' }}>

        {/* Left Panel - Contact List */}
        <div style={{ width: '380px', minWidth: '380px', background: colors.navy2, borderRight: `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column' }}>
          {/* Search & Filter */}
          <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '10px', color: colors.muted, fontSize: '12px' }} />
              <input
                type="text" placeholder="Search contacts..."
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 8px 8px 30px', color: colors.white, fontSize: '13px', outline: 'none' }}
              />
            </div>
            <div className="d-flex gap-1">
              {['all', 'new', 'engaged', 'hot', 'converted'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  style={{ background: filterStatus === s ? colors.gold : colors.navy3, color: filterStatus === s ? colors.navy : colors.muted, border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Contact List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-5" style={{ color: colors.muted }}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: '32px', marginBottom: '8px' }} /><br />
                No contacts found
              </div>
            ) : (
              contacts.map(contact => (
                <div
                  key={contact.id}
                  onClick={() => selectContact(contact)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                    background: selected?.id === contact.id ? colors.navy3 : 'transparent',
                    borderLeft: selected?.id === contact.id ? `3px solid ${colors.gold}` : '3px solid transparent'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div style={{ color: colors.white, fontWeight: 600, fontSize: '14px' }}>{contact.full_name}</div>
                      <div style={{ color: colors.muted, fontSize: '12px' }}>{contact.email}</div>
                      {contact.brokerage && <div style={{ color: colors.muted, fontSize: '11px' }}>{contact.brokerage}</div>}
                    </div>
                    <div className="text-end">
                      <div style={{ color: getScoreColor(contact.lead_score), fontWeight: 700, fontSize: '16px' }}>{contact.lead_score}</div>
                      <span style={{
                        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px',
                        color: contact.status === 'hot' ? colors.red : contact.status === 'engaged' ? colors.orange : contact.status === 'converted' ? colors.green : colors.muted
                      }}>
                        {contact.status}
                      </span>
                    </div>
                  </div>
                  {contact.last_activity_at && (
                    <div style={{ color: colors.muted, fontSize: '10px', marginTop: '4px' }}>
                      Last: {new Date(contact.last_activity_at).toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Contact Detail */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#eef1f6' }}>
          {!selected ? (
            <div style={{ padding: '24px' }}>
              {/* Dashboard Header */}
              <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '1px', marginBottom: '20px' }}>
                Contact <span style={{ color: colors.gold }}>Dashboard</span>
              </h3>

              {/* KPI Cards */}
              {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                  {[
                    { label: 'Total Contacts', value: stats.total, color: colors.blue },
                    { label: 'Hot Leads', value: stats.hot, color: '#e74c3c' },
                    { label: 'New Leads', value: stats.new_contacts, color: colors.gold },
                    { label: 'Needs Follow-up', value: stats.needs_followup, color: '#e67e22' },
                    { label: 'SMS Active (24h)', value: stats.recent_sms, color: '#2ecc71' },
                    { label: 'Converted', value: stats.converted, color: colors.green },
                  ].map((s, i) => (
                    <div key={i} style={{ background: '#f8f9fa', borderLeft: `3px solid ${s.color}`, borderRadius: '6px', padding: '12px 14px' }}>
                      <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#333', marginTop: '2px' }}>{s.value || 0}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Hot Leads List */}
              <div style={{ marginBottom: '20px' }}>
                <h6 style={{ color: colors.navy, fontWeight: 700, fontSize: '14px', marginBottom: '10px' }}>
                  <FontAwesomeIcon icon={faFire} className="me-2" style={{ color: '#e74c3c' }} />Hot Leads — Act Now
                </h6>
                {contacts.filter(c => c.status === 'hot').length === 0 ? (
                  <p style={{ color: '#999', fontSize: '13px' }}>No hot leads right now</p>
                ) : (
                  contacts.filter(c => c.status === 'hot').slice(0, 5).map(c => (
                    <div key={c.id} onClick={() => selectContact(c)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', marginBottom: '4px', background: '#fff5f5', borderRadius: '6px', cursor: 'pointer', border: '1px solid #ffcccc' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: colors.navy }}>{c.full_name}</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>{c.brokerage || c.email}</div>
                      </div>
                      <div style={{ fontWeight: 700, color: '#e74c3c', fontSize: '16px' }}>{c.lead_score}</div>
                    </div>
                  ))
                )}
              </div>

              {/* Recent Activity */}
              <div>
                <h6 style={{ color: colors.navy, fontWeight: 700, fontSize: '14px', marginBottom: '10px' }}>
                  <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: colors.blue }} />Recent Activity
                </h6>
                {contacts.filter(c => c.last_activity_at).sort((a, b) => new Date(b.last_activity_at!).getTime() - new Date(a.last_activity_at!).getTime()).slice(0, 5).map(c => (
                  <div key={c.id} onClick={() => selectContact(c)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', marginBottom: '4px', background: '#f8f9fa', borderRadius: '6px', cursor: 'pointer', border: '1px solid #eee' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: colors.navy }}>{c.full_name}</div>
                      <div style={{ fontSize: '11px', color: '#888' }}>{c.source || 'contact'}</div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#999' }}>
                      {c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : detailLoading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
          ) : (
            <div style={{ padding: '24px' }}>
              {/* Profile Header */}
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div style={{ flex: 1 }}>
                    {editingContact ? (
                      <div>
                        <div className="d-flex gap-2 mb-2">
                          <input value={editForm.full_name} onChange={e => setEditForm({ ...editForm, full_name: e.target.value })} placeholder="Full Name"
                            style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '1px', color: colors.navy, border: 'none', borderBottom: `2px solid ${colors.gold}`, background: 'transparent', outline: 'none', width: '100%' }} />
                        </div>
                        <div className="d-flex gap-2 flex-wrap mt-2">
                          <div style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Email</label>
                            <input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email"
                              style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', outline: 'none' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Phone</label>
                            <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone"
                              style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', outline: 'none' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase' }}>Brokerage</label>
                            <input value={editForm.brokerage} onChange={e => setEditForm({ ...editForm, brokerage: e.target.value })} placeholder="Brokerage"
                              style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', outline: 'none' }} />
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <Button size="sm" onClick={() => {
                            axiosInstance.put(`/contacts/${selected.id}`, editForm).then(() => {
                              setAlert({ type: 'success', message: 'Contact updated' });
                              setEditingContact(false);
                              fetchContacts();
                              selectContact({ ...selected, ...editForm } as any);
                            }).catch(() => setAlert({ type: 'danger', message: 'Failed to update' }));
                          }} style={{ background: colors.green, border: 'none', fontSize: '11px', fontWeight: 600 }}>Save</Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => setEditingContact(false)} style={{ fontSize: '11px' }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', letterSpacing: '1px', margin: 0 }}>
                          {selected.full_name}
                        </h3>
                        <div className="d-flex gap-3 mt-2 flex-wrap" style={{ fontSize: '14px' }}>
                          <span style={{ color: '#555' }}><FontAwesomeIcon icon={faEnvelope} className="me-1" style={{ color: colors.gold }} />{selected.email}</span>
                          {selected.phone && <span style={{ color: '#555' }}><FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: colors.gold }} />{selected.phone}</span>}
                          {selected.brokerage && <span style={{ color: '#555' }}><FontAwesomeIcon icon={faBuilding} className="me-1" style={{ color: colors.gold }} />{selected.brokerage}</span>}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="text-end">
                    <div style={{ color: getScoreColor(selected.lead_score), fontWeight: 700, fontSize: '32px', fontFamily: "'Bebas Neue', sans-serif" }}>{selected.lead_score}</div>
                    <Form.Select size="sm" value={selected.status} onChange={e => handleStatusChange(selected.id, e.target.value)}
                      style={{ background: 'white', color: colors.navy, border: `1px solid #ddd`, fontSize: '12px', width: '110px' }}>
                      <option value="new">New</option>
                      <option value="engaged">Engaged</option>
                      <option value="hot">Hot</option>
                      <option value="converted">Converted</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" onClick={() => setShowEmailForm(!showEmailForm)}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Send Email
                  </Button>
                  <Button size="sm" variant="outline-light" style={{ fontSize: '12px', borderColor: colors.muted, color: colors.muted }}
                    onClick={() => { setActiveTab('notes'); }}>
                    <FontAwesomeIcon icon={faStickyNote} className="me-1" /> Add Note
                  </Button>
                  <Button size="sm" variant="outline-light" style={{ fontSize: '12px', borderColor: colors.blue, color: colors.blue }}
                    onClick={() => {
                      setEditForm({ full_name: selected.full_name, email: selected.email, phone: selected.phone || '', brokerage: selected.brokerage || '' });
                      setEditingContact(true);
                    }}>
                    &#9998; Edit
                  </Button>
                  {selected.phone && (
                    <Button size="sm" variant="outline-light" style={{ fontSize: '12px', borderColor: colors.green, color: colors.green }}
                      onClick={() => {
                        if (teamCallers.length === 0) {
                          axiosInstance.get('/contacts/team-callers').then(r => {
                            if (r.data.success) setTeamCallers(r.data.callers || []);
                          });
                        }
                        setShowCallPanel(!showCallPanel);
                      }}>
                      <FontAwesomeIcon icon={faPhone} className="me-1" /> Call
                    </Button>
                  )}
                </div>

                {/* Call Panel */}
                {showCallPanel && selected?.phone && (
                  <div style={{ background: '#f0faf0', borderRadius: '8px', padding: '12px', marginTop: '10px', border: '1px solid #c8e6c9' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, marginBottom: '8px' }}>
                      <FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: colors.green }} /> Call {selected.full_name} via Conference Bridge
                    </div>
                    <div className="d-flex gap-2 align-items-end">
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '10px', color: '#666' }}>Who is calling?</label>
                        <Form.Select size="sm" value={selectedCaller} onChange={e => setSelectedCaller(e.target.value)} style={{ fontSize: '12px' }}>
                          <option value="">Select team member...</option>
                          {teamCallers.map((c: any) => (
                            <option key={c.id} value={c.user_phone}>
                              {c.user_first_name} {c.user_last_name} — {c.user_phone}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <Button size="sm" disabled={!selectedCaller || calling}
                        onClick={async () => {
                          setCalling(true);
                          try {
                            const caller = teamCallers.find((c: any) => c.user_phone === selectedCaller);
                            const res = await axiosInstance.post(`/contacts/${selected.id}/call`, {
                              callerPhone: selectedCaller,
                              callerName: caller ? `${caller.user_first_name} ${caller.user_last_name}` : 'Team Member'
                            });
                            if (res.data.success) {
                              setAlert({ type: 'success', message: `Calling ${selectedCaller} first, then connecting to ${selected.full_name}...` });
                              setShowCallPanel(false);
                            }
                          } catch (err: any) {
                            setAlert({ type: 'danger', message: err.response?.data?.error || 'Call failed' });
                          }
                          setCalling(false);
                        }}
                        style={{ background: colors.green, border: 'none', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {calling ? 'Connecting...' : '📞 Call Now'}
                      </Button>
                    </div>
                    <div style={{ fontSize: '10px', color: '#888', marginTop: '6px' }}>
                      Your phone will ring first from (520) 251-9595. Answer to connect to {selected.full_name}. Call is recorded for quality assurance.
                    </div>
                  </div>
                )}
              </div>

              {/* Inline Email Form */}
              {showEmailForm && (
                <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '20px', border: `1px solid ${colors.gold}` }}>
                  <h6 style={{ color: colors.gold2, fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Compose Email to {selected.full_name}
                  </h6>
                  <input placeholder="Subject" value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                    style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '10px', color: colors.white, fontSize: '14px', marginBottom: '8px', outline: 'none' }} />
                  <textarea placeholder={`Hi {{name}},\n\n...`} value={emailMessage} onChange={e => setEmailMessage(e.target.value)} rows={5}
                    style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '10px', color: colors.white, fontSize: '14px', marginBottom: '8px', outline: 'none', resize: 'vertical' }} />
                  <div className="d-flex gap-2">
                    <Button size="sm" onClick={handleSendEmail} disabled={sending || !emailSubject || !emailMessage}
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                      {sending ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Send</>}
                    </Button>
                    <Button size="sm" variant="outline-light" onClick={() => setShowEmailForm(false)} style={{ borderColor: colors.muted, color: colors.muted }}>Cancel</Button>
                  </div>
                  <small style={{ color: colors.muted, fontSize: '11px' }}>Use <code style={{ color: colors.gold2 }}>{'{{name}}'}</code> to personalize. Navy+gold branded template applied automatically.</small>
                </div>
              )}

              {/* Events Attended */}
              {contactEvents.length > 0 && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Events ({contactEvents.length})
                  </h6>
                  {contactEvents.map((ev, i) => (
                    <div key={i} className="d-flex justify-content-between align-items-center" style={{ padding: '6px 0', borderBottom: i < contactEvents.length - 1 ? '1px solid #eee' : 'none' }}>
                      <a href={`/admin/events`} onClick={(e) => { e.preventDefault(); window.location.href = `/admin/events?eventId=${(ev as any).event_id}`; }}
                        style={{ color: '#0d6efd', fontSize: '13px', textDecoration: 'none', cursor: 'pointer' }} title="View event details">
                        {ev.event_title}
                      </a>
                      <span style={{ color: ev.attended ? colors.green : colors.muted, fontSize: '11px', fontWeight: 600 }}>
                        {ev.attended ? '✓ Attended' : 'Registered'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Tabs */}
              <div className="d-flex gap-1 mb-3">
                {(['timeline', 'emails', 'sms', 'automation', 'notes', 'invite', 'production'] as const).map(tab => (
                  <button key={tab} onClick={() => {
                    setActiveTab(tab as any);
                    if (tab === 'sms' && smsMessages.length === 0 && selected) {
                      axiosInstance.get(`/contacts/${selected.id}/sms`).then(r => {
                        if (r.data.success) { setSmsMessages(r.data.messages || []); setCallLogs(r.data.callLogs || []); }
                      }).catch(() => {});
                    }
                    if (tab === 'automation' && !automationData && selected) {
                      axiosInstance.get(`/contacts/${selected.id}/automation`).then(r => {
                        if (r.data.success) setAutomationData(r.data.automation);
                      }).catch(() => {});
                    }
                    if (tab === 'invite' && upcomingEvents.length === 0) {
                      axiosInstance.get('/events/upcoming').then(r => {
                        if (r.data.success) setUpcomingEvents(r.data.events || []);
                      }).catch(() => {});
                    }
                    if (tab === 'production' && !mlsProduction && selected) {
                      axiosInstance.get(`/contacts/${selected.id}/mls-production`).then(r => {
                        if (r.data.success) { setMlsProfile(r.data.mlsProfile); setMlsProduction(r.data.mlsProduction); }
                      }).catch(() => {});
                    }
                  }}
                    style={{ background: activeTab === tab ? colors.gold : colors.navy3, color: activeTab === tab ? colors.navy : colors.muted, border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                    {tab === 'sms' ? 'SMS/Calls' : tab === 'automation' ? 'AI Automation' : tab === 'invite' ? 'Invite to Event' : tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e9ecef' }}>
                {activeTab === 'timeline' && (
                  <div>
                    {activities.length === 0 ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>No activity recorded yet</p>
                    ) : (
                      activities.map(act => {
                        const parsed = typeof act.activity_data === 'string' ? (() => { try { return JSON.parse(act.activity_data); } catch { return {}; } })() : (act.activity_data || {});
                        const hasDetails = parsed?.message || parsed?.note || parsed?.task;
                        return (
                          <div key={act.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <div className="d-flex gap-3 align-items-start">
                              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${getActivityColor(act.activity_type)}15`, border: `2px solid ${getActivityColor(act.activity_type)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FontAwesomeIcon icon={getActivityIcon(act.activity_type)} style={{ color: getActivityColor(act.activity_type), fontSize: '11px' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: '#333', fontSize: '13px', fontWeight: 500 }}>{formatActivityText(act.activity_type, act.activity_data)}</div>
                                {hasDetails && (
                                  <div style={{ color: '#666', fontSize: '12px', marginTop: '4px', padding: '6px 10px', background: '#f8f9fa', borderRadius: '4px', borderLeft: `2px solid ${getActivityColor(act.activity_type)}` }}>
                                    {parsed.message && <div>💬 {parsed.message.substring(0, 200)}{parsed.message.length > 200 ? '...' : ''}</div>}
                                    {parsed.task && !parsed.message && <div>📋 Task: {parsed.task}</div>}
                                    {parsed.note && <div>{parsed.note}</div>}
                                    {parsed.event_title && <div>📅 Event: {parsed.event_title}</div>}
                                    {parsed.phone && <div>📱 Phone: {parsed.phone}</div>}
                                    {parsed.brokerage && !parsed.message && <div>🏢 {parsed.brokerage}</div>}
                                  </div>
                                )}
                                <div style={{ color: '#999', fontSize: '11px', marginTop: '4px' }}>{new Date(act.created_at).toLocaleString()}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {activeTab === 'emails' && (
                  <div>
                    {emailHistory.length === 0 ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>No emails sent yet</p>
                    ) : (
                      emailHistory.map(em => (
                        <div key={em.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                          onClick={() => setExpandedEmail(expandedEmail === em.id ? null : em.id)}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div style={{ color: '#333', fontSize: '14px', fontWeight: 600 }}>{em.subject}</div>
                            <span style={{ color: '#999', fontSize: '11px' }}>{expandedEmail === em.id ? '▲' : '▼'}</span>
                          </div>
                          <div className="d-flex gap-3" style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                            <span>{new Date(em.sent_at).toLocaleString()}</span>
                            <span style={{ color: colors.gold }}>{em.template}</span>
                            <span style={{ color: colors.green }}>{em.status}</span>
                          </div>
                          {expandedEmail === em.id && em.body && (
                            <div style={{ marginTop: '10px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', borderLeft: `3px solid ${colors.gold}`, fontSize: '13px', color: '#444', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                              {em.body.replace(/\{\{name\}\}/g, selected?.full_name || '')}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'notes' && (
                  <div>
                    {/* Add note */}
                    <div className="d-flex gap-2 mb-3">
                      <input placeholder="Add a note about this contact..." value={noteText} onChange={e => setNoteText(e.target.value)}
                        style={{ flex: 1, background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', color: '#333', fontSize: '13px', outline: 'none' }}
                        onKeyDown={e => { if (e.key === 'Enter' && noteText.trim()) handleAddNote(); }} />
                      <Button size="sm" onClick={handleAddNote} disabled={!noteText.trim()}
                        style={{ background: colors.gold, border: 'none', fontWeight: 600 }}>
                        Add
                      </Button>
                    </div>
                    {/* Note history */}
                    {activities.filter(a => a.activity_type === 'note_added').length === 0 ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '10px 0' }}>No notes yet</p>
                    ) : (
                      activities.filter(a => a.activity_type === 'note_added').map(act => {
                        const parsed = typeof act.activity_data === 'string' ? JSON.parse(act.activity_data) : act.activity_data;
                        return (
                          <div key={act.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <div style={{ color: '#333', fontSize: '13px' }}>{parsed?.note}</div>
                            <div style={{ color: '#999', fontSize: '11px', marginTop: '4px' }}>{new Date(act.created_at).toLocaleString()}</div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* SMS / Calls Tab */}
                {activeTab === 'sms' && (
                  <div>
                    {/* Send via Jasper */}
                    {(selected?.phone || selected?.email) && (
                      <div style={{ background: '#f0f7ff', borderRadius: '8px', padding: '14px', marginBottom: '16px', border: '1px solid #d0e4f7' }}>
                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            🤖 Send via Jasper
                          </div>
                          <div className="d-flex gap-1">
                            <button onClick={() => { setJasperChannel('sms'); setJasperPreview(''); setJasperEmailSubject(''); }}
                              style={{ background: jasperChannel === 'sms' ? colors.navy : '#eee', color: jasperChannel === 'sms' ? 'white' : '#666', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                              disabled={!selected?.phone}>
                              📱 SMS
                            </button>
                            <button onClick={() => { setJasperChannel('email'); setJasperPreview(''); }}
                              style={{ background: jasperChannel === 'email' ? colors.navy : '#eee', color: jasperChannel === 'email' ? 'white' : '#666', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                              disabled={!selected?.email}>
                              ✉️ Email
                            </button>
                          </div>
                        </div>

                        <textarea value={jasperTask} onChange={e => { setJasperTask(e.target.value); setJasperPreview(''); }}
                          placeholder={jasperChannel === 'sms'
                            ? 'Give Jasper a task, e.g.:\n• "Schedule a meeting for Monday, get their available times"\n• "Follow up about their email connectivity question"'
                            : 'Give Jasper a task, e.g.:\n• "Send a welcome email with product overview and setup instructions"\n• "Follow up on their demo request with next steps"'}
                          rows={3} style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', resize: 'vertical', marginBottom: '8px' }} />

                        <div className="d-flex gap-2">
                          <Button size="sm" disabled={!jasperTask.trim() || jasperLoading}
                            onClick={async () => {
                              setJasperLoading(true);
                              try {
                                const res = await axiosInstance.post(`/contacts/${selected.id}/jasper-draft`, { task: jasperTask, channel: jasperChannel });
                                if (res.data.success) {
                                  setJasperPreview(res.data.draft);
                                  if (res.data.subject) setJasperEmailSubject(res.data.subject);
                                }
                              } catch (err: any) { setAlert({ type: 'danger', message: 'Failed to generate draft' }); }
                              setJasperLoading(false);
                            }}
                            style={{ background: colors.navy, border: 'none', fontSize: '11px', fontWeight: 600 }}>
                            {jasperLoading ? <><Spinner size="sm" className="me-1" />Drafting...</> : '📝 Preview Draft'}
                          </Button>
                          {jasperPreview && (
                            <Button size="sm" disabled={jasperSending}
                              onClick={async () => {
                                setJasperSending(true);
                                try {
                                  const endpoint = jasperChannel === 'email' ? 'jasper-send-email' : 'jasper-send';
                                  const payload = jasperChannel === 'email'
                                    ? { message: jasperPreview, subject: jasperEmailSubject, task: jasperTask }
                                    : { message: jasperPreview, task: jasperTask };
                                  const res = await axiosInstance.post(`/contacts/${selected.id}/${endpoint}`, payload);
                                  if (res.data.success) {
                                    setAlert({ type: 'success', message: `Jasper sent ${jasperChannel} to ${selected.full_name}` });
                                    setJasperTask(''); setJasperPreview(''); setJasperEmailSubject('');
                                    if (jasperChannel === 'sms') {
                                      axiosInstance.get(`/contacts/${selected.id}/sms`).then(r => {
                                        if (r.data.success) setSmsMessages(r.data.messages || []);
                                      });
                                    }
                                  }
                                } catch (err: any) { setAlert({ type: 'danger', message: 'Failed to send' }); }
                                setJasperSending(false);
                              }}
                              style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '11px', fontWeight: 700 }}>
                              {jasperSending ? <><Spinner size="sm" className="me-1" />Sending...</> : `📨 Send ${jasperChannel === 'email' ? 'Email' : 'SMS'}`}
                            </Button>
                          )}
                        </div>

                        {jasperPreview && (
                          <div style={{ background: 'white', borderRadius: '6px', padding: '12px', marginTop: '10px', border: '1px solid #e0e0e0' }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>
                              Jasper will send ({jasperChannel}):
                            </div>
                            {jasperChannel === 'email' && (
                              <input value={jasperEmailSubject} onChange={e => setJasperEmailSubject(e.target.value)}
                                placeholder="Email subject"
                                style={{ width: '100%', fontSize: '13px', border: '1px solid #eee', borderRadius: '4px', padding: '6px 8px', marginBottom: '6px', fontWeight: 600 }} />
                            )}
                            <textarea value={jasperPreview} onChange={e => setJasperPreview(e.target.value)}
                              rows={jasperChannel === 'email' ? 8 : 4} style={{ width: '100%', fontSize: '13px', border: '1px solid #eee', borderRadius: '4px', padding: '8px', resize: 'vertical', lineHeight: 1.5 }} />
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>Edit the {jasperChannel === 'email' ? 'subject and body' : 'message'} above if needed, then click Send.</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Call Logs */}
                    {callLogs.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                          <FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: colors.gold }} /> Call History
                        </div>
                        {callLogs.map((call: any) => (
                          <div key={call.id} style={{ padding: '8px 12px', marginBottom: '6px', background: '#f8f9fa', borderRadius: '6px', borderLeft: `3px solid ${call.direction === 'inbound' ? colors.blue : colors.gold}`, fontSize: '12px' }}>
                            <div className="d-flex justify-content-between">
                              <span style={{ fontWeight: 600 }}>{call.direction === 'inbound' ? '📞 Incoming' : '📱 Outgoing'}</span>
                              <span style={{ color: '#999', fontSize: '11px' }}>{new Date(call.created_at).toLocaleString()}</span>
                            </div>
                            {call.duration && <div style={{ color: '#666', marginTop: '2px' }}>Duration: {call.duration}s</div>}
                            {call.transcript && <div style={{ color: '#555', marginTop: '4px', fontStyle: 'italic', fontSize: '11px' }}>"{call.transcript.substring(0, 150)}..."</div>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* SMS Conversation */}
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                      💬 SMS Conversation {smsMessages.length > 0 && `(${smsMessages.length} messages)`}
                    </div>
                    {smsMessages.length === 0 ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>No SMS history for this contact</p>
                    ) : (
                      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px 0' }}>
                        {smsMessages.map((msg: any) => {
                          const isKira = msg.direction === 'outbound';
                          const meta = typeof msg.metadata === 'string' ? JSON.parse(msg.metadata || '{}') : (msg.metadata || {});
                          return (
                            <div key={msg.id} style={{
                              display: 'flex', justifyContent: isKira ? 'flex-start' : 'flex-end', marginBottom: '8px'
                            }}>
                              <div style={{
                                maxWidth: '80%', padding: '10px 14px', borderRadius: isKira ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                                background: isKira ? '#f0f0f0' : colors.navy, color: isKira ? '#333' : '#fff', fontSize: '13px', lineHeight: 1.5
                              }}>
                                <div style={{ fontWeight: 600, fontSize: '10px', marginBottom: '4px', color: isKira ? colors.gold : colors.gold2 }}>
                                  {isKira ? '🤖 Kira' : '👤 ' + (selected?.full_name || 'Contact')}
                                  {meta.drip_step && <span style={{ marginLeft: '6px', fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: '#ff980020', color: '#ff9800' }}>drip: {meta.drip_step}</span>}
                                </div>
                                {msg.body}
                                <div style={{ fontSize: '10px', color: isKira ? '#999' : '#8FA3BF', marginTop: '4px', textAlign: 'right' }}>
                                  {new Date(msg.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* AI Automation Tab */}
                {activeTab === 'automation' && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                      🤖 AI Sales Automation — Kira
                    </div>

                    {!automationData ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>Loading automation status...</p>
                    ) : automationData.dripStatus === 'no_phone' ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>No phone number on file — SMS automation requires a phone number</p>
                    ) : (
                      <>
                        {/* Status Card */}
                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '12px', border: '1px solid #e9ecef' }}>
                          <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                            <span style={{ fontWeight: 700, fontSize: '14px', color: colors.navy }}>Drip Status</span>
                            <span style={{
                              fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px',
                              background: automationData.dripStatus === 'active_conversation' ? '#e8fde8' : automationData.dripStatus === 'awaiting_reply' ? '#fff3e0' : '#f0f0f0',
                              color: automationData.dripStatus === 'active_conversation' ? '#27ae60' : automationData.dripStatus === 'awaiting_reply' ? '#f39c12' : '#888'
                            }}>
                              {automationData.dripStatus === 'active_conversation' ? '🟢 Active Conversation' :
                               automationData.dripStatus === 'awaiting_reply' ? '🟡 Awaiting Reply' :
                               automationData.dripStatus === 'recent_outreach' ? '🔵 Recent Outreach' :
                               '⚪ No Drip Active'}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div style={{ background: 'white', borderRadius: '6px', padding: '10px 12px', border: '1px solid #eee' }}>
                              <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase' }}>Last Outreach</div>
                              <div style={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>
                                {automationData.hoursSinceLastOutbound != null ? `${automationData.hoursSinceLastOutbound}h ago` : 'Never'}
                              </div>
                            </div>
                            <div style={{ background: 'white', borderRadius: '6px', padding: '10px 12px', border: '1px solid #eee' }}>
                              <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase' }}>Follow-ups Sent</div>
                              <div style={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>
                                {automationData.dripFollowUpsSent} / {automationData.maxDrips}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Next Step */}
                        {automationData.nextDripStep && (
                          <div style={{ background: '#fff3e0', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', border: '1px solid #ffcc80' }}>
                            <div style={{ fontWeight: 700, fontSize: '12px', color: '#e65100' }}>Next Automated Action</div>
                            <div style={{ fontSize: '13px', color: '#333', marginTop: '4px' }}>
                              {automationData.nextDripStep.type === 'gentle_nudge' && '💬 Gentle follow-up — "Any questions I can help with?"'}
                              {automationData.nextDripStep.type === 'value_add' && '🎯 Value add — share upcoming event or feature highlight'}
                              {automationData.nextDripStep.type === 'direct_offer' && '🔥 Direct offer — signup link with urgency'}
                              {automationData.nextDripStep.type === 'final_close' && '👋 Final message — leave the door open, no pressure'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                              Status: {automationData.nextDripStep.due} {automationData.schedulerEnabled ? '(scheduler active)' : '(scheduler disabled — manual only)'}
                            </div>
                          </div>
                        )}

                        {/* Drip Timeline */}
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Drip Sequence</div>
                        {[
                          { step: 1, label: 'Gentle Nudge', timing: '+4 hours', desc: 'Check if they have questions' },
                          { step: 2, label: 'Value Add', timing: '+24 hours', desc: 'Event invite or feature highlight' },
                          { step: 3, label: 'Direct Offer', timing: '+3 days', desc: 'Signup link with urgency' },
                          { step: 4, label: 'Final Close', timing: '+7 days', desc: 'Graceful final message' },
                        ].map(s => {
                          const completed = s.step <= automationData.dripFollowUpsSent;
                          const isCurrent = s.step === automationData.dripFollowUpsSent + 1;
                          return (
                            <div key={s.step} style={{
                              display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #eee'
                            }}>
                              <div style={{
                                width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0,
                                background: completed ? colors.green : isCurrent ? colors.gold : '#eee',
                                color: completed || isCurrent ? 'white' : '#999'
                              }}>
                                {completed ? '✓' : s.step}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: completed ? colors.green : isCurrent ? colors.gold : '#666' }}>{s.label}</div>
                                <div style={{ fontSize: '11px', color: '#999' }}>{s.timing} — {s.desc}</div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Scheduler Status */}
                        <div style={{ marginTop: '16px', padding: '10px 12px', background: automationData.schedulerEnabled ? '#e8fde8' : '#fee', borderRadius: '6px', border: `1px solid ${automationData.schedulerEnabled ? '#c8e6c9' : '#ffcdd2'}`, fontSize: '12px' }}>
                          <span style={{ fontWeight: 700 }}>Scheduler: </span>
                          {automationData.schedulerEnabled
                            ? '✅ Active — automated follow-ups running Mon-Fri 8am-4pm AZ time'
                            : '🛑 Disabled — no automated follow-ups. Enable at /api/kira/scheduler/enable'}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Invite to Event Tab */}
                {activeTab === 'invite' && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                      📨 Invite {selected?.full_name} to an Event via Jasper
                    </div>

                    {!selected?.phone ? (
                      <div style={{ background: '#fff3e0', borderRadius: '8px', padding: '16px', border: '1px solid #ffcc80' }}>
                        <p style={{ color: '#e65100', fontSize: '13px', margin: 0 }}>
                          This contact has no phone number on file. Add one via the Edit button to send SMS invites.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Event Selection */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Select Event</label>
                          <Form.Select value={inviteEventId} onChange={e => setInviteEventId(e.target.value)}
                            style={{ fontSize: '13px' }}>
                            <option value="">Choose an upcoming event...</option>
                            {upcomingEvents.map((e: any) => (
                              <option key={e.id} value={e.id}>
                                {e.title} — {new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {e.ticket_price > 0 ? ` ($${e.ticket_price})` : ' (Free)'}
                                {` — ${e.current_registrations}/${e.max_capacity} registered`}
                              </option>
                            ))}
                          </Form.Select>
                        </div>

                        {/* Personal Note */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px', display: 'block' }}>Personal Note (optional)</label>
                          <textarea value={inviteNote} onChange={e => setInviteNote(e.target.value)}
                            placeholder={`e.g., "Based on our conversation about AI tools, I think this session would be a perfect fit for your business..."`}
                            rows={3} style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', resize: 'vertical' }} />
                        </div>

                        {/* Preview */}
                        {inviteEventId && (() => {
                          const evt = upcomingEvents.find((e: any) => e.id === inviteEventId);
                          if (!evt) return null;
                          const evtDate = new Date(evt.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                          return (
                            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '12px', border: '1px solid #e9ecef' }}>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: '6px' }}>Preview — Jasper will send:</div>
                              <div style={{ fontSize: '13px', color: '#333', lineHeight: 1.6 }}>
                                Hi {selected?.full_name || 'there'}! This is Jasper from KairosIQ AI.
                                {inviteNote ? ` ${inviteNote}` : ` We have an upcoming session I think you'd love:`}
                                <br /><br />
                                📚 {evt.title}<br />
                                📅 {evtDate}{evt.event_time ? ` at ${evt.event_time}` : ''}{evt.ticket_price > 0 ? ` ($${evt.ticket_price})` : ' (Free)'}
                                <br /><br />
                                👉 Register: app.kairosiq.ai/events/{evt.id}
                                <br /><br />
                                — Jasper, KairosIQ AI
                              </div>
                            </div>
                          );
                        })()}

                        {/* Send Button */}
                        <Button
                          disabled={!inviteEventId || sendingInvite}
                          onClick={async () => {
                            if (!inviteEventId || !selected) return;
                            setSendingInvite(true);
                            try {
                              const evt = upcomingEvents.find((e: any) => e.id === inviteEventId);
                              const res = await axiosInstance.post(`/contacts/${selected.id}/invite-to-event`, {
                                eventId: evt.id, eventTitle: evt.title, eventDate: evt.event_date, eventTime: evt.event_time,
                                eventUrl: `https://app.kairosiq.ai/events/${evt.id}`, ticketPrice: evt.ticket_price,
                                personalNote: inviteNote || undefined
                              });
                              if (res.data.success) {
                                setAlert({ type: 'success', message: `Jasper sent invite to ${selected.full_name} for ${evt.title}` });
                                setInviteEventId('');
                                setInviteNote('');
                                // Refresh timeline
                                selectContact(selected);
                              }
                            } catch (err: any) {
                              setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send invite' });
                            }
                            setSendingInvite(false);
                          }}
                          style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, fontSize: '14px', padding: '10px 24px', width: '100%' }}>
                          {sendingInvite ? <><Spinner size="sm" className="me-2" />Sending...</> : '📨 Send Invite via Jasper'}
                        </Button>

                        {/* Previous invites */}
                        {activities.filter(a => a.activity_type === 'event_invite_sent').length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: '8px' }}>Previous Invites Sent</div>
                            {activities.filter(a => a.activity_type === 'event_invite_sent').map(act => {
                              const parsed = typeof act.activity_data === 'string' ? JSON.parse(act.activity_data) : act.activity_data;
                              return (
                                <div key={act.id} style={{ padding: '8px 0', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                                  <span style={{ fontWeight: 600 }}>{parsed?.event_title || 'Event'}</span>
                                  <span style={{ color: '#999', marginLeft: '8px' }}>{new Date(act.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'production' && mlsProduction && (
                  <div>
                    {/* MLS Profile header */}
                    {mlsProfile && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '14px', border: '1px solid #eee' }}>
                        {mlsProfile.photo_url ? (
                          <img src={mlsProfile.photo_url} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: `${colors.navy}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: colors.muted }}>
                            {(mlsProfile.first_name || '?')[0]}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700, color: colors.navy, fontSize: '14px' }}>{mlsProfile.member_name}</div>
                          <div style={{ fontSize: '11px', color: colors.muted }}>
                            {mlsProfile.office_name} · {mlsProfile.local_type || 'Agent'} · MLS: {mlsProfile.short_id}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.muted }}>
                            License: {mlsProfile.license_number || '—'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Generate Report Button */}
                    <button
                      onClick={async () => {
                        try {
                          const btn = document.getElementById('agent-report-btn') as HTMLButtonElement;
                          if (btn) { btn.disabled = true; btn.textContent = 'Generating Report…'; }
                          const res = await axiosInstance.get(`/contacts/${selected.id}/production-report`, { responseType: 'blob' });
                          const ct = res.headers['content-type'] || '';
                          const ext = ct.includes('pdf') ? 'pdf' : 'docx';
                          const blob = new Blob([res.data], { type: ct });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(selected.full_name || 'Agent').replace(/[^a-zA-Z0-9]/g, '_')}_Production_Report.${ext}`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (err) {
                          console.error('Report generation failed:', err);
                          setAlert({ type: 'danger', message: 'Report generation failed. Please try again.' });
                        } finally {
                          const btn = document.getElementById('agent-report-btn') as HTMLButtonElement;
                          if (btn) { btn.disabled = false; btn.textContent = 'Generate Production Report (PDF)'; }
                        }
                      }}
                      id="agent-report-btn"
                      style={{
                        background: `linear-gradient(135deg, ${colors.gold}, #E8B44A)`,
                        color: '#0F1E35', border: 'none', borderRadius: '8px',
                        padding: '10px 20px', fontSize: '13px', fontWeight: 700,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                        marginBottom: '14px', fontFamily: 'inherit',
                      }}
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                      Generate Production Report (PDF)
                    </button>

                    {/* KPIs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                      {[
                        { label: 'Total Deals', value: mlsProduction.total_deals.toLocaleString(), color: colors.gold },
                        { label: 'Total Volume', value: `$${(mlsProduction.total_volume / 1000000).toFixed(1)}M`, color: '#27ae60' },
                        { label: 'Avg Price', value: `$${mlsProduction.avg_price.toLocaleString()}`, color: '#3498db' },
                        { label: 'YTD Deals', value: mlsProduction.ytd_deals, color: '#f39c12' },
                        { label: 'YTD Volume', value: `$${(mlsProduction.ytd_volume / 1000000).toFixed(1)}M`, color: '#27ae60' },
                        { label: 'Avg DOM', value: `${mlsProduction.avg_dom}d`, color: colors.muted },
                      ].map((s, i) => (
                        <div key={i} style={{ background: '#f8f9fa', borderLeft: `3px solid ${s.color}`, borderRadius: '6px', padding: '10px 12px' }}>
                          <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                          <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginTop: '2px' }}>{s.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Listing vs Buyer */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                      <div style={{ background: '#f0f7ff', borderRadius: '6px', padding: '10px 14px', border: '1px solid #d0e4f7' }}>
                        <div style={{ fontSize: '10px', color: '#3498db', textTransform: 'uppercase', fontWeight: 700 }}>Listing Side</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{mlsProduction.listing_deals} deals</div>
                      </div>
                      <div style={{ background: '#f0faf0', borderRadius: '6px', padding: '10px 14px', border: '1px solid #d0e8d0' }}>
                        <div style={{ fontSize: '10px', color: '#27ae60', textTransform: 'uppercase', fontWeight: 700 }}>Buyer Side</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{mlsProduction.buyer_deals} deals</div>
                      </div>
                    </div>

                    {/* By year */}
                    {mlsProduction.by_year?.length > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Deals by Year</div>
                        {mlsProduction.by_year.map((y: any, i: number) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                            <span style={{ fontWeight: 600 }}>{y.year}</span>
                            <span style={{ color: '#888' }}>{y.deals} deals · <strong style={{ color: colors.gold }}>${(y.volume / 1000000).toFixed(1)}M</strong></span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recent deals */}
                    {mlsProduction.recent_deals?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Recent Deals</div>
                        {mlsProduction.recent_deals.map((d: any, i: number) => (
                          <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 500, color: '#333', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address || '—'}</span>
                              <span style={{ fontWeight: 700, color: colors.gold }}>${d.price?.toLocaleString()}</span>
                            </div>
                            <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>
                              {d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                              {d.beds ? ` · ${d.beds}bd/${d.baths}ba` : ''}
                              {d.sqft ? ` · ${d.sqft.toLocaleString()} sqft` : ''}
                              {d.dom ? ` · ${d.dom}d DOM` : ''}
                              <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: d.side === 'listing' ? '#e8f4fd' : '#e8fde8', color: d.side === 'listing' ? '#3498db' : '#27ae60', textTransform: 'uppercase' }}>{d.side}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Email Overlay */}
      {showBulkEmail && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: colors.navy2, borderRadius: '12px', padding: '32px', width: '600px', maxWidth: '90vw', border: `1px solid ${colors.gold}` }}>
            <h4 style={{ color: colors.white, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', marginBottom: '4px' }}>
              Email All <span style={{ color: colors.gold2 }}>Contacts</span>
            </h4>
            <p style={{ color: colors.muted, fontSize: '13px', marginBottom: '20px' }}>
              {filterStatus !== 'all' ? `Sending to all "${filterStatus}" contacts` : 'Sending to all active contacts'}. Use <code style={{ color: colors.gold2 }}>{'{{name}}'}</code> to personalize.
            </p>
            <input placeholder="Subject" value={bulkSubject} onChange={e => setBulkSubject(e.target.value)}
              style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '12px', color: colors.white, fontSize: '14px', marginBottom: '10px', outline: 'none' }} />
            <textarea placeholder="Message..." value={bulkMessage} onChange={e => setBulkMessage(e.target.value)} rows={8}
              style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '12px', color: colors.white, fontSize: '14px', marginBottom: '16px', outline: 'none', resize: 'vertical' }} />
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-light" onClick={() => setShowBulkEmail(false)} style={{ borderColor: colors.muted, color: colors.muted }}>Cancel</Button>
              <Button onClick={handleBulkEmail} disabled={bulkSending || !bulkSubject || !bulkMessage}
                style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700 }}>
                {bulkSending ? <><Spinner size="sm" className="me-1" />Sending...</> : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Send to All</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
